import type { Stripe, StripeCardElement, StripeElementStyle } from '@stripe/stripe-js'

import { theme } from '#tailwind-config'

export default ({ focusOnReady }: { focusOnReady?: boolean } = {}) => {
  const { country } = useAuth()
  const { language } = useLanguage()
  const { data: config, suspense } = useQueryConfig.current()
  const stripeInstance = ref<Stripe>()
  const cardInstance = ref<StripeCardElement>()
  const isValid = ref<boolean>()
  const isComplete = ref<boolean>(false)

  const initStripe = async () => {
    const { loadStripe } = await import('@stripe/stripe-js')

    await suspense() // TODO remove this in DEV-5806
    const key = config.value?.stripe_publishable_key

    if (!key)
      throw new Error(`No stripe key for country: ${country.value}`)

    return await loadStripe(key) || undefined
  }

  const initializeCardElement = async (target: HTMLElement, styleOpts?: StripeElementStyle) => {
    if (cardInstance.value)
      return

    const cardStyle = styleOpts ?? {
      base: {
        fontSize: '16px',
        color: theme.colors.gray[800],
        fontSmoothing: 'antialiased',
      },
    } satisfies StripeElementStyle

    if (!stripeInstance.value)
      stripeInstance.value = await initStripe()

    cardInstance.value = stripeInstance.value?.elements({ locale: language.value }).create('card', {
      hidePostalCode: true,
      style: cardStyle,
    })

    cardInstance.value?.on('change', ({ complete }) => {
      if (!isValid.value && !complete)
        isValid.value = undefined

      isComplete.value = complete
    })

    cardInstance.value?.on('blur', () => {
      isValid.value = isComplete.value
    })

    cardInstance.value?.on('ready', () => {
      if (focusOnReady)
        cardInstance.value?.focus()
    })

    cardInstance.value?.mount(target)
  }

  const uninitializeCardElement = () => {
    if (!cardInstance.value)
      return

    cardInstance.value.unmount()
    cardInstance.value = undefined
  }

  const getPaymentMethod = async () => {
    if (!stripeInstance.value)
      stripeInstance.value = await initStripe()

    if (!cardInstance.value)
      return Promise.reject(new Error('CardInstance not found'))

    return await stripeInstance.value?.createPaymentMethod({
      type: 'card',
      card: cardInstance.value,
    }).then(res => res.error ? Promise.reject(res.error) : res.paymentMethod)
  }

  const confirmCardSetup = async (
    clientSecret: string,
    paymentMethodId: string | undefined,
  ) => await stripeInstance.value?.confirmCardSetup(clientSecret, { payment_method: paymentMethodId })

  const handleCardAction = async (clientSecret: string) => await stripeInstance.value?.handleCardAction(clientSecret)

  return {
    initializeCardElement,
    uninitializeCardElement,
    getPaymentMethod,
    confirmCardSetup,
    handleCardAction,
    initStripe,
    cardInstance,
    isValid,
    isComplete,
  }
}
